import React from "react";
import { FileIconComponent } from "../../../../../../components/file-icon/file-icon.component";
import {
	getFileType,
	getFileTypeWithDocumentType,
} from "@/lib/utils/file-utils";
import { useStorage } from "../../../../../../hooks/useStorage";
import { FilePreview } from "@components/file-preview/file-preview.component";
import {
	ArrowDownToLine,
	Share,
	SquareArrowOutUpRight,
	Trash2,
} from "lucide-react";
import { IconButton, Tooltip } from "@mui/material";

export type FileGallerySize = "small" | "medium" | "large";

export interface FileGalleryItem {
	id: string;
	file_name: string | null;
	path_name: string | null;
	url: string;
	meta_data?: unknown;
	shared_with?: string | null;
	shared_until?: string | null;
}

const SIZE_CONFIG: Record<
	FileGallerySize,
	{ px: number; previewWidth: number; className: string }
> = {
	small: { px: 96, previewWidth: 100, className: "h-24 w-24" },
	medium: { px: 144, previewWidth: 150, className: "h-36 w-36" },
	large: { px: 192, previewWidth: 200, className: "h-48 w-48" },
};

interface FilePreviewProps {
	file: FileGalleryItem;
	size: FileGallerySize;
	messageOfCurrentOrg?: boolean;
	sharedUntil?: string | null;
	alwaysShowPreview?: boolean;
	onShareFile?: (fileId: string) => void;
	onUnshareFile?: (fileId: string) => void;
	onFileClick?: (fileId: string) => void;
	onDeleteFile?: (fileId: string) => void;
}

export const FileInGallery = ({
	file,
	size,
	messageOfCurrentOrg = false,
	sharedUntil,
	alwaysShowPreview = false,
	onShareFile,
	onUnshareFile,
	onFileClick,
	onDeleteFile,
}: FilePreviewProps) => {
	const { downloadFromSupabase } = useStorage();
	const sc = SIZE_CONFIG[size];
	const dimensions = sc.className;
	const boxWidthStyle = { width: `${sc.px}px` };
	const iconSize = size === "large" ? 32 : 24;
	const metaData = (file?.meta_data ?? null) as unknown as Record<
		string,
		any
	> | null;

	const effectiveSharedUntil = sharedUntil ?? file.shared_until ?? null;
	const isActivelyShared = file.shared_with
		? !file.shared_until
		: sharedUntil === null;

	if (effectiveSharedUntil && !alwaysShowPreview) {
		return (
			<div style={boxWidthStyle}>
				<div
					className={`flex flex-col gap-2 ${dimensions} items-center justify-center text-sm font-semibold text-gray-700 border-gray-400 border rounded-md p-2 w-full`}
				>
					<div>
						<Tooltip
							title={`Das Teilen der Datei wurde am ${new Date(
								effectiveSharedUntil!
							).toLocaleDateString()} um ${new Date(
								effectiveSharedUntil!
							).toLocaleTimeString([], {
								hour: "2-digit",
								minute: "2-digit",
							})} Uhr aufgehoben. ${
								messageOfCurrentOrg && onShareFile
									? "Datei erneut teilen?"
									: ""
							}`}
						>
							{messageOfCurrentOrg && onShareFile ? (
								<div>
									<IconButton
										onClick={() => onShareFile(file.id)}
										size="small"
									>
										<Share size={18} />
									</IconButton>
								</div>
							) : (
								<div className="text-center">
									Nicht verfügbar
								</div>
							)}
						</Tooltip>
					</div>
				</div>

				{messageOfCurrentOrg && onShareFile && (
					<div
						className="text-sm text-gray-700"
						style={boxWidthStyle}
					>
						{file.file_name}
					</div>
				)}
			</div>
		);
	}

	const fileExt = file.file_name?.split(".").pop()?.toUpperCase();

	return (
		<div className="flex flex-col">
			<div className="relative" style={boxWidthStyle}>
				<div
					className={`${dimensions} rounded-md overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center border border-gray-200`}
					onClick={() => onFileClick?.(file.id)}
				>
					{file.url ? (
						<FilePreview
							url={file.url}
							fileType={getFileType(metaData, file.file_name)}
							fileName={file.file_name || undefined}
							width={sc.previewWidth}
						/>
					) : (
						<div className="flex flex-col items-center justify-center gap-1 animate-pulse">
							<FileIconComponent
								fileType={getFileTypeWithDocumentType(
									metaData,
									file.file_name
								)}
								size={iconSize}
							/>
							{fileExt && (
								<span className="text-[9px] text-gray-400 font-medium">
									{fileExt}
								</span>
							)}
						</div>
					)}

					<div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100 rounded-md">
						<div className="grid grid-cols-2 gap-1">
							<Tooltip title="Öffnen">
								<div
									className="bg-white rounded-full p-1 shadow-md cursor-pointer"
									onClick={(e) => {
										e.stopPropagation();
										onFileClick?.(file.id);
									}}
								>
									<SquareArrowOutUpRight
										size={iconSize * 0.8}
									/>
								</div>
							</Tooltip>

							<Tooltip title="Herunterladen">
								<div
									className="bg-white rounded-full p-1 shadow-md cursor-pointer"
									onClick={() =>
										file.path_name &&
										downloadFromSupabase(file.path_name)
									}
								>
									<ArrowDownToLine
										size={iconSize * 0.8}
										className="text-gray-700"
									/>
								</div>
							</Tooltip>

							{messageOfCurrentOrg &&
								onUnshareFile &&
								isActivelyShared && (
									<Tooltip title="Nicht mehr teilen">
										<div
											className="bg-white rounded-full p-1 shadow-md cursor-pointer relative"
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												onUnshareFile(file.id);
											}}
										>
											<Share
												size={iconSize * 0.8}
												className="text-gray-700"
											/>
											<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
												<div className="h-0.5 w-5 bg-black rotate-45 transform-gpu rounded-full"></div>
											</div>
										</div>
									</Tooltip>
								)}
							{messageOfCurrentOrg &&
								onShareFile &&
								!isActivelyShared && (
									<Tooltip title="Datei teilen">
										<div
											className="bg-white rounded-full p-1 shadow-md cursor-pointer"
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												onShareFile(file.id);
											}}
										>
											<Share
												size={iconSize * 0.8}
												className="text-gray-700"
											/>
										</div>
									</Tooltip>
								)}
							{onDeleteFile && (
								<Tooltip title="Löschen">
									<div
										className="bg-white rounded-full p-1 shadow-md cursor-pointer"
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											onDeleteFile(file.id);
										}}
										aria-label={`Datei ${file.file_name} löschen`}
										data-testid={`file-gallery-delete-${file.id}`}
									>
										<Trash2
											size={iconSize * 0.8}
											className="text-red-500"
										/>
									</div>
								</Tooltip>
							)}
						</div>
					</div>
				</div>
			</div>

			<Tooltip title={file.file_name || "Datei"}>
				<div
					className="mt-1 px-1 text-xs font-medium text-gray-700 truncate"
					style={boxWidthStyle}
				>
					{file.file_name
						? file.file_name.length >
							(size === "small"
								? 18
								: size === "medium"
									? 32
									: 48)
							? `${file.file_name.substring(
									0,
									size === "small"
										? 12
										: size === "medium"
											? 22
											: 34
								)}...${file.file_name.substring(
									file.file_name.lastIndexOf(".")
								)}`
							: file.file_name
						: "Datei"}
				</div>
			</Tooltip>
		</div>
	);
};
